import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "quote__preview"
}
const _hoisted_2 = ["alt", "src"]
const _hoisted_3 = { class: "quote__main" }
const _hoisted_4 = {
  key: 0,
  class: "quote__main-author",
  role: "heading",
  "aria-level": "4"
}
const _hoisted_5 = { class: "quote__main-author-info" }
const _hoisted_6 = { class: "quote__main-author-name" }
const _hoisted_7 = {
  role: "blockquote",
  dir: "auto",
  class: "quote__main-text"
}

import type { ChatMessage } from '../types/index.ts'

import { t } from '@nextcloud/l10n'
import { generateUrl } from '@nextcloud/router'
import { computed, ref, toRef } from 'vue'
import { useRoute } from 'vue-router'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcLoadingIcon from '@nextcloud/vue/components/NcLoadingIcon'
import IconClose from 'vue-material-design-icons/Close.vue'
import IconPencilOutline from 'vue-material-design-icons/PencilOutline.vue'
import AvatarWrapper from './AvatarWrapper/AvatarWrapper.vue'
import { useMessageInfo } from '../composables/useMessageInfo.ts'
import { AVATAR } from '../constants.ts'
import { EventBus } from '../services/EventBus.ts'
import { useActorStore } from '../stores/actor.ts'
import { useChatExtrasStore } from '../stores/chatExtras.ts'
import { getMessageIcon } from '../utils/getMessageIcon.ts'
import { parseToSimpleMessage } from '../utils/textParse.ts'

type DeletedParentMessage = Pick<ChatMessage, 'id' | 'deleted'>


export default /*@__PURE__*/_defineComponent({
  __name: 'MessageQuote',
  props: {
    message: {},
    canCancel: { type: Boolean, default: false },
    editMessage: { type: Boolean, default: false }
  },
  setup(__props: any) {



const route = useRoute()
const actorStore = useActorStore()
const chatExtrasStore = useChatExtrasStore()

const {
	isFileShare,
	isObjectShare,
	remoteServer,
	actorDisplayName,
	actorDisplayNameWithFallback,
} = useMessageInfo(isExistingMessage(__props.message) ? toRef(() => __props.message) : undefined)

const actorInfo = computed(() => [actorDisplayNameWithFallback.value, remoteServer.value].filter((value) => value).join(' '))

const hash = computed(() => '#message_' + __props.message.id)

const component = computed(() => __props.canCancel
	? { tag: 'div', link: undefined }
	: { tag: 'router-link', link: { query: route.query, hash: hash.value } })

const isOwnMessageQuoted = computed(() => isExistingMessage(__props.message) ? actorStore.checkIfSelfIsActor(__props.message) : false)

const filePreviewLoading = ref(true)
const filePreviewFailed = ref(false)
const filePreview = computed(() => {
	if (!isExistingMessage(__props.message) || !isFileShare || filePreviewFailed.value) {
		return undefined
	}

	const fileData = Object.values(__props.message.messageParameters).find((param) => param.type === 'file' && param['preview-available'] === 'yes')
	if (fileData) {
		return {
			alt: fileData.name,
			src: generateUrl('/core/preview?fileId={fileId}&x=32&y=32&a=1', { fileId: fileData.id }),
		}
	} else {
		return undefined
	}
})

const simpleQuotedMessageIcon = computed(() => isExistingMessage(__props.message) ? getMessageIcon(__props.message) : null)

const editLabel = computed(() => {
	if (__props.editMessage) {
		return t('spreed', '(editing)')
	} else if (isExistingMessage(__props.message) && __props.message.lastEditTimestamp) {
		return t('spreed', '(edited)')
	}
	return ''
})

/**
 * This is a simplified version of the last chat message.
 * Parameters are parsed without markup (just replaced with the name),
 * e.g. no avatars on mentions.
 *
 * @return A simple message to show below the conversation name
 */
const simpleQuotedMessage = computed(() => {
	if (!isExistingMessage(__props.message)) {
		return t('spreed', 'The message has expired or has been deleted')
	} else {
		return parseToSimpleMessage(__props.message.message, __props.message.messageParameters)
	}
})

/**
 * Shorten the message to 250 characters and append three dots to the end of the
 * string. This is needed because on very wide screens, if the 250 characters
 * fit, the css rules won't ellipsize the text-overflow.
 */
const shortenedQuoteMessage = computed(() => {
	return simpleQuotedMessage.value.length >= 250 ? simpleQuotedMessage.value.substring(0, 250) + '…' : simpleQuotedMessage.value
})

/**
 * Check whether message to quote (parent) existing on server
 * Otherwise server returns ['id' => (int)$parentId, 'deleted' => true]
 *
 * @param message
 */
function isExistingMessage(message: ChatMessage | DeletedParentMessage): message is ChatMessage {
	return 'messageType' in message
}

/**
 * Abort replying / editing process
 */
function handleAbort() {
	if (!isExistingMessage(__props.message)) {
		return
	}

	if (__props.editMessage) {
		chatExtrasStore.removeMessageIdToEdit(__props.message.token)
	} else {
		chatExtrasStore.removeParentIdToReply(__props.message.token)
	}
	EventBus.emit('focus-chat-input')
}

/**
 * Focus quoted message
 */
function handleQuoteClick() {
	if (__props.canCancel) {
		return
	}

	if (route.hash === hash.value) {
		// Already on this message route, just trigger highlight
		EventBus.emit('focus-message', { messageId: __props.message.id })
	}
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_resolveDynamicComponent(component.value.tag), {
    to: component.value.link,
    class: _normalizeClass(["quote", { 'quote--own-message': isOwnMessageQuoted.value }]),
    onClick: handleQuoteClick
  }, {
    default: _withCtx(() => [
      (_unref(isFileShare) || _unref(isObjectShare))
        ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
            (filePreview.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  class: "quote__preview-image",
                  alt: filePreview.value.alt,
                  src: filePreview.value.src,
                  onLoad: _cache[0] || (_cache[0] = ($event: any) => (filePreviewLoading.value = false)),
                  onError: _cache[1] || (_cache[1] = ($event: any) => {filePreviewLoading.value = false; filePreviewFailed.value = true})
                }, null, 40, _hoisted_2))
              : (simpleQuotedMessageIcon.value)
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(simpleQuotedMessageIcon.value), {
                    key: 1,
                    class: "quote__preview-image",
                    "fill-color": "var(--color-text-maxcontrast)",
                    size: 34
                  }))
                : _createCommentVNode("", true),
            (filePreview.value && filePreviewLoading.value)
              ? (_openBlock(), _createBlock(_unref(NcLoadingIcon), {
                  key: 2,
                  class: "quote__preview--loading"
                }))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("span", _hoisted_3, [
        (isExistingMessage(__props.message))
          ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
              (__props.editMessage)
                ? (_openBlock(), _createBlock(IconPencilOutline, {
                    key: 0,
                    size: 16
                  }))
                : (!(_unref(isFileShare) || _unref(isObjectShare)))
                  ? (_openBlock(), _createBlock(AvatarWrapper, {
                      key: 1,
                      id: __props.message.actorId,
                      token: __props.message.token,
                      name: _unref(actorDisplayName),
                      source: __props.message.actorType,
                      size: _unref(AVATAR).SIZE.EXTRA_SMALL,
                      "disable-menu": ""
                    }, null, 8, ["id", "token", "name", "source", "size"]))
                  : _createCommentVNode("", true),
              _createElementVNode("span", _hoisted_5, [
                _createElementVNode("span", _hoisted_6, _toDisplayString(actorInfo.value), 1),
                _createTextVNode(" " + _toDisplayString(editLabel.value), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("span", _hoisted_7, _toDisplayString(shortenedQuoteMessage.value), 1)
      ]),
      (__props.canCancel)
        ? (_openBlock(), _createBlock(_unref(NcButton), {
            key: 1,
            class: "quote__button",
            variant: "tertiary",
            title: _unref(t)('spreed', 'Cancel quote'),
            "aria-label": _unref(t)('spreed', 'Cancel quote'),
            onClick: handleAbort
          }, {
            icon: _withCtx(() => [
              _createVNode(IconClose, { size: 20 })
            ]),
            _: 1
          }, 8, ["title", "aria-label"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["to", "class"]))
}
}

})